import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";
import RichTextEditor from "../caseStudy/RichTextEditor";

/* ─── API ────────────────────────────────────────────────────────────── */
const pagesApi = {
  get:             (id)           => axiosInstance.get(`/api/superadmin/pages/${id}/`),
  create:          (body)         => axiosInstance.post("/api/superadmin/pages/", body),
  update:          (id, body)     => axiosInstance.put(`/api/superadmin/pages/${id}/`, body),
  publish:         (id)           => axiosInstance.post(`/api/superadmin/pages/${id}/publish/`),
  unpublish:       (id)           => axiosInstance.post(`/api/superadmin/pages/${id}/unpublish/`),
  listSections:    (pageId)       => axiosInstance.get(`/api/superadmin/pages/${pageId}/sections/`),
  createSection:   (pageId, body) => axiosInstance.post(`/api/superadmin/pages/${pageId}/sections/`, body),
  updateSection:   (pageId, id, body) => axiosInstance.put(`/api/superadmin/pages/${pageId}/sections/${id}/`, body),
  deleteSection:   (pageId, id)   => axiosInstance.delete(`/api/superadmin/pages/${pageId}/sections/${id}/`),
  listAssets:      (pageId)       => axiosInstance.get(`/api/superadmin/pages/${pageId}/assets/`),
  uploadAsset:     (pageId, body) => axiosInstance.post(`/api/superadmin/pages/${pageId}/assets/`, body, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteAsset:     (pageId, id)   => axiosInstance.delete(`/api/superadmin/pages/${pageId}/assets/${id}/`),
};
const pageTypesApi = {
  list: () => axiosInstance.get("/api/superadmin/page-types/"),
};
const pageSectionTypesApi = {
  list: () => axiosInstance.get("/api/superadmin/page-section-types/"),
};

const PAGE_STATUSES = ["draft", "published", "archived"];

const formatOptionLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const extractErrorMessage = (err, fallback = "Save failed.") => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === "string") return data;
  if (data.message || data.detail) return data.message || data.detail;
  const errors = data.errors || data;
  const messages = Object.entries(errors)
    .flatMap(([field, value]) => {
      const items = Array.isArray(value) ? value : [value];
      return items.map((item) => `${field}: ${typeof item === "string" ? item : JSON.stringify(item)}`);
    });
  return messages.join(", ") || fallback;
};

/* ─── Toast (inline minimal) ───────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add  = useCallback((type, title, msg) => { const id = Date.now(); setToasts((p) => [...p, { id, type, title, msg }]); setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4000); }, []);
  const Toasts = () => (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map((t) => {
        const bar = t.type === "success" ? "#16a34a" : t.type === "error" ? "#dc2626" : "#1d4ed8";
        return (
          <div key={t.id} style={{ background: "#fff", borderLeft: `4px solid ${bar}`, borderRadius: 10, padding: "12px 16px", minWidth: 280, boxShadow: "0 4px 20px rgba(0,0,0,.1)" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{t.title}</div>
            {t.msg && <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>{t.msg}</div>}
          </div>
        );
      })}
    </div>
  );
  return { success: (t, m) => add("success", t, m), error: (t, m) => add("error", t, m), info: (t, m) => add("info", t, m), Toasts };
}

/* ─── Input helpers ─────────────────────────────────────────────────── */
const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", boxSizing: "border-box", color: "#1e293b", fontFamily: "inherit" };
const labelStyle = { display: "block", marginBottom: 5, fontSize: 13, fontWeight: 600, color: "#374151" };
const cardStyle  = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" };

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}{required && <span style={{ color: "#dc2626" }}> *</span>}</label>
      {children}
    </div>
  );
}

const SECTION_DEFAULTS = {
  hero: { eyebrow: "", heading: "", description: "", primary_cta_label: "", primary_cta_url: "", image_url: "", image_alt: "" },
  rich_text: { heading: "", body: "" },
  features: { heading: "", items: [{ title: "", description: "", image_url: "" }] },
  stats: { heading: "", items: [{ value: "", label: "" }] },
  cta: { heading: "", description: "", button_label: "", button_url: "", image_url: "" },
  gallery: { heading: "", images: [{ url: "", alt: "" }] },
  testimonials: { source: "api", api_url: "/api/testimonials/", limit: 6 },
  case_studies: { source: "api", api_url: "/api/case-studies/", limit: 6 },
};

function SectionDataFields({ type, data, onChange }) {
  const value = { ...(SECTION_DEFAULTS[type] || {}), ...(data || {}) };
  const set = (key, next) => onChange({ ...value, [key]: next });
  const input = (key, label, placeholder = "") => <Field label={label}><input value={value[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} style={inputStyle} /></Field>;
  if (type === "hero") return <><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{input("eyebrow", "Eyebrow")}{input("heading", "Heading")}</div><Field label="Description"><RichTextEditor value={value.description || ""} onChange={(next) => set("description", next)} placeholder="Write the hero description..." minHeight={120} /></Field><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{input("primary_cta_label", "Button label")}{input("primary_cta_url", "Button URL", "/contact")}{input("image_url", "Image URL")}{input("image_alt", "Image alt text")}</div></>;
  if (type === "rich_text") return <><Field label="Heading"><input value={value.heading || ""} onChange={(e) => set("heading", e.target.value)} style={inputStyle} /></Field><Field label="Content"><RichTextEditor value={value.body || ""} onChange={(next) => set("body", next)} placeholder="Write the section content..." minHeight={220} /></Field></>;
  if (type === "cta") return <><Field label="Heading"><input value={value.heading || ""} onChange={(e) => set("heading", e.target.value)} style={inputStyle} /></Field><Field label="Description"><RichTextEditor value={value.description || ""} onChange={(next) => set("description", next)} placeholder="Write the call-to-action description..." minHeight={120} /></Field><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{input("button_label", "Button label")}{input("button_url", "Button URL", "/contact")}{input("image_url", "Image URL")}</div></>;
  if (["testimonials", "case_studies"].includes(type)) return <><p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}>This section displays live content from its API; content is not copied into the page.</p>{input("api_url", "API endpoint", type === "testimonials" ? "/api/testimonials/" : "/api/case-studies/")}<Field label="Maximum items"><input type="number" min="1" value={value.limit || 6} onChange={(e) => set("limit", Number(e.target.value))} style={inputStyle} /></Field></>;
  if (["features", "stats", "gallery"].includes(type)) {
    const itemKey = type === "gallery" ? "images" : "items";
    const items = value[itemKey] || [];
    const update = (index, key, next) => set(itemKey, items.map((item, i) => i === index ? { ...item, [key]: next } : item));
    return <><Field label="Heading"><input value={value.heading || ""} onChange={(e) => set("heading", e.target.value)} style={inputStyle} /></Field>{items.map((item, index) => <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}><input value={item.title || item.value || item.url || ""} onChange={(e) => update(index, type === "stats" ? "value" : type === "gallery" ? "url" : "title", e.target.value)} placeholder={type === "stats" ? "Value" : type === "gallery" ? "Image URL" : "Title"} style={inputStyle} /><input value={item.description || item.label || item.alt || ""} onChange={(e) => update(index, type === "stats" ? "label" : type === "gallery" ? "alt" : "description", e.target.value)} placeholder={type === "stats" ? "Label" : type === "gallery" ? "Alt text" : "Description"} style={inputStyle} /><button onClick={() => set(itemKey, items.filter((_, i) => i !== index))} style={{ border: "1px solid #fecaca", color: "#dc2626", background: "#fff", borderRadius: 6 }}>Remove</button></div>)}<button onClick={() => set(itemKey, [...items, type === "stats" ? { value: "", label: "" } : type === "gallery" ? { url: "", alt: "" } : { title: "", description: "", image_url: "" }])} style={{ border: "1px solid #c7d2fe", color: "#4338ca", background: "#eef2ff", borderRadius: 6, padding: "7px 10px" }}>Add item</button></>;
  }
  return <Field label="Content"><textarea value={JSON.stringify(value, null, 2)} onChange={(e) => { try { onChange(JSON.parse(e.target.value)); } catch {} }} rows={6} style={{ ...inputStyle, fontFamily: "monospace" }} /></Field>;
}

/* ─── Section Editor ────────────────────────────────────────────────── */
function SectionRow({ section, pageId, sectionTypes, onSaved, onDeleted, idx }) {
  const [open, setOpen]     = useState(false);
  const [form, setForm]     = useState(section);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save() {
    setSaving(true);
    try {
      const body = { section_type: form.section_type, title: form.title, data: form.data || {}, sort_order: Number(form.sort_order), is_active: form.is_active };
      const { data } = await pagesApi.updateSection(pageId, section.id, body);
      onSaved(data.data ?? data);
      toast.success("Saved", "Section updated.");
      setOpen(false);
    } catch (err) {
      toast.error("Error", err?.response?.data?.message ?? "Save failed.");
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm("Delete this section?")) return;
    try { await pagesApi.deleteSection(pageId, section.id); onDeleted(section.id); } catch { toast.error("Error", "Delete failed."); }
  }

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 10, background: open ? "#fafaff" : "#fff" }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", cursor: "pointer", userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, background: "#ede9fe", color: "#7c3aed", padding: "2px 9px", borderRadius: 10 }}>{section.section_type_label || formatOptionLabel(section.section_type)}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{section.title || <span style={{ color: "#94a3b8" }}>Untitled</span>}</span>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>order: {section.sort_order}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={(e) => { e.stopPropagation(); remove(); }} style={{ background: "none", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>Delete</button>
          <span style={{ color: "#94a3b8", fontSize: 13 }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          <toast.Toasts />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px", gap: 12, marginBottom: 12 }}>
            <Field label="Type"><select value={form.section_type} onChange={(e) => setForm((f) => ({ ...f, section_type: e.target.value }))} style={inputStyle}>{sectionTypes.filter((t) => t.is_active !== false || t.slug === form.section_type).map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}</select></Field>
            <Field label="Title"><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Section title" /></Field>
            <Field label="Order"><input type="number" min="0" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} style={inputStyle} /></Field>
          </div>
          <SectionDataFields type={form.section_type} data={form.data} onChange={(data) => setForm((f) => ({ ...f, data }))} />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ fontSize: 12.5, color: "#475569", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} /> Active
            </label>
            <div style={{ flex: 1 }} />
            <button onClick={() => setOpen(false)} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12.5, cursor: "pointer" }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? .7 : 1 }}>{saving ? "Saving…" : "Save Section"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Add Section Form ──────────────────────────────────────────────── */
function AddSectionForm({ pageId, sectionTypes, onAdded }) {
  const defaultSectionType = sectionTypes.find((type) => type.is_active !== false)?.slug || "";
  const [form, setForm] = useState({ section_type: defaultSectionType, title: "", sort_order: 0, data: SECTION_DEFAULTS[defaultSectionType] || {}, is_active: true });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const body = { section_type: form.section_type, title: form.title, data: form.data || {}, sort_order: Number(form.sort_order), is_active: form.is_active };
      const { data } = await pagesApi.createSection(pageId, body);
      onAdded(data.data ?? data);
      setForm({ section_type: defaultSectionType, title: "", sort_order: 0, data: SECTION_DEFAULTS[defaultSectionType] || {}, is_active: true });
      setOpen(false);
    } catch (err) {
      alert(err?.response?.data?.message ?? "Failed to add section.");
    } finally { setSaving(false); }
  }

  if (!open) return <button onClick={() => setOpen(true)} disabled={!defaultSectionType} title={!defaultSectionType ? "Create an active Page Section Master first." : "Add Section"} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "2px dashed #e2e8f0", background: "#fafbff", color: "#7c3aed", fontSize: 13, fontWeight: 600, cursor: defaultSectionType ? "pointer" : "not-allowed", opacity: defaultSectionType ? 1 : .5 }}>+ Add Section</button>;

  return (
    <div style={{ border: "2px dashed #c4b5fd", borderRadius: 10, padding: "16px", background: "#fafaff", marginBottom: 10 }}>
      <p style={{ margin: "0 0 12px", fontWeight: 700, color: "#7c3aed", fontSize: 13 }}>New Section</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px", gap: 12, marginBottom: 12 }}>
        <Field label="Type *"><select value={form.section_type} onChange={(e) => setForm((f) => ({ ...f, section_type: e.target.value }))} style={inputStyle}>{sectionTypes.filter((type) => type.is_active !== false).map((type) => <option key={type.slug} value={type.slug}>{type.name}</option>)}</select></Field>
        <Field label="Title"><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Section title" /></Field>
        <Field label="Order"><input type="number" min="0" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} style={inputStyle} /></Field>
      </div>
      <SectionDataFields type={form.section_type} data={form.data} onChange={(data) => setForm((f) => ({ ...f, data }))} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={() => setOpen(false)} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12.5, cursor: "pointer" }}>Cancel</button>
        <button onClick={save} disabled={saving} style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? .7 : 1 }}>{saving ? "Adding…" : "Add Section"}</button>
      </div>
    </div>
  );
}

/* ─── Main Page Editor ──────────────────────────────────────────────── */
function AssetLibrary({ pageId, toast }) {
  const [assets, setAssets] = useState([]);
  const [file, setFile] = useState(null);
  const [altText, setAltText] = useState("");
  const [uploading, setUploading] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await pagesApi.listAssets(pageId); setAssets(data?.results ?? data?.data ?? data ?? []); } catch { toast.error("Assets", "Could not load uploaded images."); }
  }, [pageId, toast]);
  useEffect(() => { load(); }, [load]);
  async function upload() {
    if (!file) return;
    setUploading(true);
    try { const body = new FormData(); body.append("image", file); body.append("alt_text", altText); await pagesApi.uploadAsset(pageId, body); setFile(null); setAltText(""); await load(); toast.success("Uploaded", "Image is ready to use in a section."); } catch { toast.error("Upload failed", "Select a valid image and try again."); } finally { setUploading(false); }
  }
  return <div style={cardStyle}><p style={{ margin: "0 0 8px", fontWeight: 700, fontSize: 16, color: "#0f172a" }}>Image Library</p><p style={{ margin: "0 0 14px", color: "#64748b", fontSize: 12.5 }}>Upload page images, then paste the image URL into a section field.</p><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end", marginBottom: 14 }}><Field label="Image"><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} style={inputStyle} /></Field><Field label="Alt text"><input value={altText} onChange={(e) => setAltText(e.target.value)} style={inputStyle} /></Field><button onClick={upload} disabled={!file || uploading} style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: "#3b82f6", color: "#fff", fontWeight: 600, cursor: "pointer", opacity: !file || uploading ? .6 : 1 }}>{uploading ? "Uploading..." : "Upload"}</button></div>{assets.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>{assets.map((asset) => <div key={asset.id} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 8 }}><img src={asset.image_url} alt={asset.alt_text} style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 5 }} /><input readOnly value={asset.image_url} onFocus={(e) => e.target.select()} style={{ ...inputStyle, marginTop: 6, fontSize: 10, padding: "5px 6px" }} /><button onClick={async () => { await pagesApi.deleteAsset(pageId, asset.id); load(); }} style={{ marginTop: 6, width: "100%", border: "1px solid #fecaca", borderRadius: 5, background: "#fff", color: "#dc2626", padding: 5, fontSize: 11 }}>Delete</button></div>)}</div>}</div>;
}

export default function PageEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const isNew    = !slug;
  const toast    = useToast();
  const returnTo = location.state?.returnTo || "/cms/pages?tab=pages";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [acting, setActing]   = useState(false);
  const [page, setPage]       = useState(null);
  const [sections, setSections] = useState([]);
  const [pageTypes, setPageTypes] = useState([]);
  const [sectionTypes, setSectionTypes] = useState([]);

  const [form, setForm] = useState({
    title: "", page_type: "", status: "draft",
    is_homepage: false, is_active: true,
    seo_title: "", seo_description: "", seo_keywords: "", canonical_url: "", robots: "index,follow", og_image_url: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await pageTypesApi.list();
        const types = data?.results ?? [];
        setPageTypes(types);
        const firstActive = types.find((type) => type.is_active !== false)?.slug || "";
        if (isNew && firstActive) {
          setForm((prev) => ({ ...prev, page_type: prev.page_type || firstActive }));
        }
      } catch {
        toast.error("Load failed", "Could not load page types.");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await pageSectionTypesApi.list();
        setSectionTypes(data?.results ?? []);
      } catch {
        toast.error("Load failed", "Could not load page section types.");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Load existing page */
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await pagesApi.get(slug);
        const p = data?.data ?? data;
        setPage(p);
        setForm({
          title: p.title ?? "",
          page_type: p.page_type ?? "",
          status: p.status ?? "draft",
          is_homepage: !!p.is_homepage,
          is_active: p.is_active !== false,
          seo_title: p.seo_title ?? "",
          seo_description: p.seo_description ?? "",
          seo_keywords: p.seo_keywords ?? "",
          canonical_url: p.canonical_url ?? "",
          robots: p.robots ?? "index,follow",
          og_image_url: p.og_image_url ?? "",
        });
        setSections(p.sections ?? []);
      } catch {
        toast.error("Load failed", "Could not load page.");
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  /* Save page meta */
  async function savePage() {
    if (!form.title.trim()) { toast.error("Validation", "Title is required."); return; }
    if (!form.page_type) { toast.error("Validation", "Create or select a page type first."); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await pagesApi.create(form);
        const created = data?.data ?? data;
        toast.success("Created", "Page created. You can now add sections.");
        navigate(`/cms/pages/${created.slug}`, { replace: true, state: { returnTo } });
      } else {
        const { data } = await pagesApi.update(page.slug, form);
        setPage(data?.data ?? data);
        toast.success("Saved", "Page updated.");
      }
    } catch (err) {
      console.error("Page save failed", err?.response?.data || err);
      toast.error("Error", extractErrorMessage(err));
    } finally { setSaving(false); }
  }

  /* Publish / Unpublish */
  async function togglePublish() {
    setActing(true);
    try {
      if (form.status === "published") {
        const { data } = await pagesApi.unpublish(page.slug);
        const updated = data?.data ?? data;
        setForm((f) => ({ ...f, status: updated.status ?? "draft" }));
        toast.success("Unpublished", "Page is now a draft.");
      } else {
        const { data } = await pagesApi.publish(page.slug);
        const updated = data?.data ?? data;
        setForm((f) => ({ ...f, status: updated.status ?? "published" }));
        toast.success("Published", "Page is now live.");
      }
    } catch (err) {
      toast.error("Error", err?.response?.data?.message ?? "Action failed.");
    } finally { setActing(false); }
  }

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  if (loading) return <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: 40, background: "#f8fafc", minHeight: "100vh", textAlign: "center", color: "#94a3b8" }}>Loading...</div>;

  const isPublished = form.status === "published";

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: 24, background: "#f8fafc", minHeight: "100vh", color: "#1e293b" }}>
      <toast.Toasts />

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <button onClick={() => navigate(returnTo)} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10, padding: 0, background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            <span style={{ fontSize: 17, lineHeight: 1 }}>{"<"}</span> Back to Page Master
          </button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{isNew ? "Add Page" : "Edit Page"}</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            {isNew ? "Create a CMS page and save it before adding sections" : <>Manage page details, SEO, and sections for <strong style={{ color: "#475569" }}>{page?.title ?? ""}</strong>{page?.slug ? <> / <code>{page.slug}</code></> : null}</>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!isNew && (
            <button onClick={togglePublish} disabled={acting}
              style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: isPublished ? "#d97706" : "#16a34a", color: "#fff", fontWeight: 600, fontSize: 14, cursor: acting ? "not-allowed" : "pointer", opacity: acting ? .7 : 1 }}>
              {acting ? "..." : isPublished ? "Unpublish" : "Publish"}
            </button>
          )}
          <button onClick={savePage} disabled={saving}
            style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff", fontWeight: 600, fontSize: 14, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? .7 : 1 }}>
            {saving ? "Saving..." : isNew ? "Add Page" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Page Meta */}
      <div style={cardStyle}>
        <p style={{ margin: "0 0 16px", fontWeight: 700, fontSize: 16, color: "#0f172a" }}>Page Details</p>
        <Field label="Title" required>
          <input value={form.title} onChange={f("title")} placeholder="Page title" style={inputStyle} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Page Type">
            <select value={form.page_type} onChange={f("page_type")} style={inputStyle}>
              {!form.page_type && <option value="">Select Page Type</option>}
              {pageTypes.filter((type) => type.is_active !== false || type.slug === form.page_type).map((type) => (
                <option key={type.slug} value={type.slug}>
                  {type.name || formatOptionLabel(type.slug)}
                </option>
              ))}
              {form.page_type && !pageTypes.some((type) => type.slug === form.page_type) && (
                <option value={form.page_type}>{formatOptionLabel(form.page_type)}</option>
              )}
              {pageTypes.length === 0 && <option value={form.page_type}>{formatOptionLabel(form.page_type)}</option>}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={f("status")} style={inputStyle}>
              {PAGE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {formatOptionLabel(s)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          <label style={{ fontSize: 12.5, color: "#475569", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_homepage} onChange={f("is_homepage")} /> Set as Homepage
          </label>
          <label style={{ fontSize: 12.5, color: "#475569", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_active} onChange={f("is_active")} /> Active
          </label>
        </div>
      </div>

      {/* SEO */}
      <div style={cardStyle}>
        <p style={{ margin: "0 0 16px", fontWeight: 700, fontSize: 16, color: "#0f172a" }}>SEO / Meta</p>
        <Field label="SEO Title">
          <input value={form.seo_title} onChange={f("seo_title")} placeholder="Defaults to page title if empty" style={inputStyle} />
        </Field>
        <Field label="Meta Description">
          <textarea value={form.seo_description} onChange={f("seo_description")} rows={3} placeholder="Short description for search engines" style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Keywords">
            <input value={form.seo_keywords} onChange={f("seo_keywords")} placeholder="comma, separated, keywords" style={inputStyle} />
          </Field>
          <Field label="Canonical URL"><input value={form.canonical_url} onChange={f("canonical_url")} placeholder="https://buyeroutreach.roboman.ai/case-studies" style={inputStyle} /></Field>
          <Field label="Robots"><select value={form.robots} onChange={f("robots")} style={inputStyle}><option value="index,follow">Index, follow</option><option value="noindex,follow">Noindex, follow</option><option value="noindex,nofollow">Noindex, nofollow</option></select></Field>
          <Field label="OG Image URL">
            <input value={form.og_image_url} onChange={f("og_image_url")} placeholder="https://…" style={inputStyle} />
          </Field>
        </div>
      </div>

      {/* Sections — only after page is saved */}
      {!isNew && page && <AssetLibrary pageId={page.id} toast={toast} />}
      {!isNew && page && (
        <div style={cardStyle}>
          <p style={{ margin: "0 0 16px", fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
            Sections <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 13 }}>({sections.length})</span>
          </p>

          {sections.length === 0 && (
            <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 12 }}>No sections yet. Add one below.</p>
          )}

          {sections.map((sec, idx) => (
            <SectionRow
              key={sec.id}
              section={sec}
              pageId={page.id}
              sectionTypes={sectionTypes}
              idx={idx}
              onSaved={(updated) => setSections((prev) => prev.map((s) => s.id === updated.id ? updated : s))}
              onDeleted={(deletedId) => setSections((prev) => prev.filter((s) => s.id !== deletedId))}
            />
          ))}

          <AddSectionForm
            pageId={page.id}
            sectionTypes={sectionTypes}
            onAdded={(newSec) => setSections((prev) => [...prev, newSec])}
          />
        </div>
      )}
      {isNew && (
        <div style={{ background: "#f8fafc", border: "1px dashed #e2e8f0", borderRadius: 10, padding: 18, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
          Save the page first, then you can add sections.
        </div>
      )}
    </div>
  );
}
